import { cn } from '@/lib/cn';
import type { CRBattle } from '@/types/qbot';

interface BattleRowProps {
  battle: CRBattle;
  /** Tag of the player whose POV we render (normalised, with or without '#') */
  playerTag: string;
  onClick?: () => void;
}

function formatRelativeTime(iso: string): string {
  // CR battleTime format: "20260520T214500.000Z" → parse manually.
  const m = iso.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/);
  if (!m) return '';
  const date = new Date(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}Z`);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export function BattleRow({ battle, playerTag, onClick }: BattleRowProps) {
  const normalisedTag = `#${playerTag.replace(/^#/, '').toUpperCase()}`;
  const isTeam0 = battle.team[0]?.tag?.toUpperCase() === normalisedTag;
  const me = isTeam0 ? battle.team[0] : battle.opponent[0];
  const opp = isTeam0 ? battle.opponent[0] : battle.team[0];
  if (!me || !opp) return null;

  const won = me.crowns > opp.crowns;
  const draw = me.crowns === opp.crowns;
  const resultLabel = draw ? 'Draw' : won ? 'Won' : 'Lost';
  const resultClass = draw
    ? 'bg-amber-500/20 text-amber-300'
    : won
      ? 'bg-emerald-500/20 text-emerald-300'
      : 'bg-rose-500/20 text-rose-300';

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg bg-bg-secondary p-3 text-left transition-opacity active:opacity-80',
        onClick ? 'cursor-pointer' : 'cursor-default',
      )}
    >
      <span className={cn('rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide', resultClass)}>
        {resultLabel}
      </span>
      <div className="flex flex-1 flex-col min-w-0">
        <span className="truncate text-sm font-semibold text-fg">{opp.name}</span>
        <span className="truncate text-[11px] text-fg-muted">{battle.gameMode?.name ?? battle.type}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-base font-bold tabular-nums text-fg">{me.crowns}</span>
        <span className="text-fg-muted">–</span>
        <span className="text-base font-bold tabular-nums text-fg-muted">{opp.crowns}</span>
      </div>
      <span className="w-8 text-right text-[11px] text-fg-muted">{formatRelativeTime(battle.battleTime)}</span>
    </button>
  );
}
